import { Link } from 'react-router-dom'
import Logo from '@/components/shared/Logo'

const nav = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'scheduling', label: 'Scheduling' },
  { id: 'authorizations', label: 'Authorizations' },
  { id: 'compliance', label: 'Compliance & Reporting' },
]

export default function Documentation() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            <nav aria-label="Breadcrumb" className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">Home</Link></li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-200 font-medium">Documentation</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Docs</p>
              <nav className="space-y-2 text-sm">
                {nav.map((n) => (
                  <a key={n.id} href={`#${n.id}`} className="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                    {n.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main docs */}
          <section className="lg:col-span-9">
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-10">
              {/* Getting Started */}
              <section id="getting-started" aria-labelledby="gs-title">
                <h2 id="gs-title" className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Getting Started</h2>
                <ol className="list-decimal pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Sign up for an AsiliFlow account at asiliflow.com/signup.</li>
                  <li>Add your agency information in Settings.</li>
                  <li>Add caregivers from Dashboard → Caregivers.</li>
                  <li>Add clients from Dashboard → Clients.</li>
                  <li>Create authorizations for each client.</li>
                  <li>Start scheduling shifts from the Schedule page.</li>
                </ol>
              </section>

              {/* Scheduling */}
              <section id="scheduling" aria-labelledby="sched-title">
                <h2 id="sched-title" className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Scheduling</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  AsiliFlow's scheduling system helps you manage shifts efficiently with conflict detection and recurring patterns.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Create one-time or recurring shifts (daily, weekly, bi-weekly, monthly)</li>
                  <li>Automatic conflict detection for caregivers and clients</li>
                  <li>Open Shifts feature for last-minute coverage needs</li>
                  <li>GPS-verified clock in/out for shift documentation</li>
                  <li>Real-time schedule updates across all devices</li>
                </ul>
              </section>

              {/* Authorizations */}
              <section id="authorizations" aria-labelledby="auth-title">
                <h2 id="auth-title" className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Authorizations</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Track client service authorizations to ensure compliance and prevent over-utilization.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Track total hours, used hours, and remaining hours</li>
                  <li>Automatic alerts 30/14/7 days before expiration</li>
                  <li>Prevent scheduling beyond authorization limits</li>
                  <li>Support for multiple authorizations per client</li>
                  <li>Real-time utilization dashboards</li>
                </ul>
              </section>

              {/* Compliance */}
              <section id="compliance" aria-labelledby="comp-title">
                <h2 id="comp-title" className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Compliance & Reporting</h2>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Complete audit trails for all shifts and changes</li>
                  <li>Shift Summary Reports for any date range</li>
                  <li>Authorization Utilization Reports</li>
                  <li>Caregiver Activity Reports</li>
                  <li>Incident Reports with documentation</li>
                  <li>Export to PDF or CSV for audits</li>
                </ul>
              </section>

              {/* CTA */}
              <div className="pt-2">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 rounded"
                >
                  Need more details? Contact Support →
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
